// cleaners.tsx
import {
  Course,
  Module,
  Target,
  Question,
  VALID_DEEP_LINKING_LEVELS,
  VALID_COMPLETION_VS_ACCURACY,
  VALID_GRADING_TYPES,
} from "./courseBuilderInterfaces";

const normalizeString = (value: any): string => {
  return String(value ?? "").trim();
};

const parseAndValidateMetadata = (
  value: any,
  context: string,
  index: number,
  id?: any
) => {
  const i = index + 2;
  const ref = id ?? "N/A";

  // Allow empty
  if (value === undefined || value === null || value === "") {
    return null;
  }

  // Parse string JSON
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);

      // Optional: ensure it's an object
      if (typeof parsed !== "object" || Array.isArray(parsed)) {
        throw new Error();
      }

      return parsed;
    } catch {
      throw new Error(
        `${context} validation failed at index ${i} (${ref}): Invalid JSON metadata`
      );
    }
  }

  // Allow object
  if (typeof value === "object") {
    if (Array.isArray(value)) {
      throw new Error(
        `${context} validation failed at index ${i} (${ref}): Metadata must be an object, not array`
      );
    }
    return value;
  }

  // Reject everything else
  throw new Error(
    `${context} validation failed at index ${i} (${ref}): Invalid metadata type. Expected JSON, received "${typeof value}"`
  );
};

export const cleanCourse = (courses: Course[]) => {
  return courses.map((course, index) => {
    const i = index + 2;
    const ref = `course_id: ${course.course_id ?? "N/A"}`;

    // deep_linking_level → metadata.deeplink_asset_type "submodule" → "target" REST map directly
    const deeplinkMap: Record<string, "target" | "module" | "course"> = {
      submodule: "target",
      module: "module",
      course: "course",
    };
    const normalizedDeepLinkLevel = normalizeString(
      course.deep_linking_level
    ).toLowerCase();
    if (!VALID_DEEP_LINKING_LEVELS.includes(normalizedDeepLinkLevel as any)) {
      throw new Error(
        `Course validation failed at index ${i} (${ref}): Invalid "deep_linking_level" value "${
          course.deep_linking_level
        }". Allowed values: ${VALID_DEEP_LINKING_LEVELS.join(", ")}.`
      );
    }

    // completion_vs_accuracy → metadata.lti_grading_method "completion" → "completion" "accuracy"
    // "graded-quizzes-points" (default; score_type refinement can be added later)
    const normalizedCompletionVsAccuracy = normalizeString(
      course.completion_vs_accuracy
    ).toLowerCase();
    if (!VALID_COMPLETION_VS_ACCURACY.includes(normalizedCompletionVsAccuracy as any)) {
      throw new Error(
        `Course validation failed at index ${i} (${ref}): Invalid "completion_vs_accuracy" value "${
          course.completion_vs_accuracy
        }". Allowed values: ${VALID_COMPLETION_VS_ACCURACY.join(", ")}.`
      );
    }
    const ltiGradingMethod =
      normalizedCompletionVsAccuracy === "accuracy"
        ? "graded-quizzes-points"
        : "completion";

    // course_name → name (required).
    const name = normalizeString(course.course_name);
    if (!name) {
      throw new Error(
        `Course validation failed at index ${i} (${ref}): Missing required field "course_name".`
      );
    }

    const parsedMetadata = parseAndValidateMetadata(
      course.metadata,
      "Course",
      index,
      ref
    );

    // grading_enabled — TRUE means send grades to Canvas, FALSE means block stored as inverse: grading_enabled=TRUE → lti_block_grade_passback=false
    const gradingEnabledRaw = course.grading_enabled;
    let gradingEnabled: boolean;
    if (typeof gradingEnabledRaw === "boolean") {
      gradingEnabled = gradingEnabledRaw;
    } else if (typeof gradingEnabledRaw === "string") {
      gradingEnabled = gradingEnabledRaw.trim().toUpperCase() === "TRUE";
    } else {
      throw new Error(
        `Course validation failed at index ${i} (${ref}): Missing required field "grading_enabled". Expected TRUE or FALSE.`
      );
    }

    // higher_ed — optional, defaults to true if blank; coerce "TRUE"/"FALSE" string to boolean
    let higherEd: boolean;
    if (
      course.higher_ed === undefined ||
      course.higher_ed === null ||
      course.higher_ed === ""
    ) {
      higherEd = true;
    } else if (typeof course.higher_ed === "string") {
      higherEd = course.higher_ed.trim().toUpperCase() === "TRUE";
    } else {
      higherEd = course.higher_ed;
    }

    // grading_type → metadata.lti_grading_trigger  "deferred" / "incremental" map directly; "n/a" omits the trigger key "n/a" is only valid when grading_enabled=FALSE OR deep_linking_level=submodule
    const normalizedGradingType = normalizeString(course.grading_type).toLowerCase();
    if (!VALID_GRADING_TYPES.includes(normalizedGradingType as any)) {
      throw new Error(
        `Course validation failed at index ${i} (${ref}): Invalid "grading_type" value "${
          course.grading_type
        }". Allowed values: ${VALID_GRADING_TYPES.join(", ")}.`
      );
    }
    if (
      normalizedGradingType === "n/a" &&
      gradingEnabled === true &&
      normalizedDeepLinkLevel !== "submodule"
    ) {
      throw new Error(
        `Course validation failed at index ${i} (${ref}): "grading_type" can only be "n/a" when grading_enabled is FALSE or deep_linking_level is "submodule".`
      );
    }
    const ltiGradingTrigger =
      normalizedGradingType === "n/a" ? undefined : normalizedGradingType;

    return {
      course_id: course.course_id ? Number(course.course_id) : undefined,
      name, // mapped from template column 'course_name'
      higher_ed: higherEd,
      metadata: {
        ...(parsedMetadata ?? {}),
        deeplink_asset_type: deeplinkMap[normalizedDeepLinkLevel],
        lti_grading_method: ltiGradingMethod,
        lti_block_grade_passback: !gradingEnabled, // inverse: enabled=TRUE → block=false
        ...(ltiGradingTrigger !== undefined && {
          lti_grading_trigger: ltiGradingTrigger,
        }),
      },
    };
  });
};

export const cleanModules = (modules: Module[]): Module[] => {
  return modules.map((module, index) => ({
    ...module,
    module_id: normalizeString(module.module_id),
    metadata: parseAndValidateMetadata(
      module.metadata,
      "Module",
      index,
      `module_id: ${module.module_id ?? "N/A"}`
    ),
  }));
};

export const cleanTargets = (targets: Target[]): Target[] => {
  return targets.map((target, index) => ({
    ...target,
    module_id: normalizeString(target.module_id),
    target_id: normalizeString(target.target_id),
    metadata: parseAndValidateMetadata(
      target.metadata,
      "Target/Submodules",
      index,
      `target_id: ${target.target_id ?? "N/A"}`
    ),
  }));
};

export const cleanQuestions = (questions: Question[]): Question[] => {
  return questions.map((question, index) => ({
    ...question,
    problem_id: normalizeString(question.problem_id),
    grd_problem_id: normalizeString(`${question.grd_problem_id}`),
    grd_template_id: normalizeString(`${question.grd_template_id}`),
    target_id: normalizeString(question.target_id),
    primary_videos: Number(question.primary_videos ?? 0),
    supplementary_videos: Number(question.supplementary_videos ?? 0),
    primary_quizzes: Number(question.primary_quizzes ?? 0),
    supplementary_quizzes: Number(question.supplementary_quizzes ?? 0),
    estimated_time_minutes: Number(question.estimated_time_minutes ?? 0),
    metadata: parseAndValidateMetadata(
      question.metadata,
      "Questions/Learning Maps",
      index,
      `target_id: ${question.target_id ?? "N/A"}`
    ),
  }));
};
